/**
 * AGC: RPC Rate Limiter - Handles 429 errors and provides caching for read-only calls
 * AGC: Reduces load on public Base Sepolia RPC endpoint
 */

interface CacheEntry<T> {
  data: T
  timestamp: number
}

interface RateLimitConfig {
  maxRetries: number
  baseDelayMs: number
  cacheDurationMs: number
}

const DEFAULT_CONFIG: RateLimitConfig = {
  maxRetries: 3,
  baseDelayMs: 1000,
  cacheDurationMs: 30000 // 30 seconds
}

const SHOULD_LOG_CACHE_HITS =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEBUG_RPC === 'true') ||
  (typeof process !== 'undefined' && process.env?.VITE_DEBUG_RPC === 'true')

// AGC: In-memory cache for read-only contract calls
const cache = new Map<string, CacheEntry<any>>()

/**
 * AGC: Exponential backoff delay calculation
 */
const getBackoffDelay = (attempt: number, baseDelayMs: number): number => {
  return baseDelayMs * Math.pow(2, attempt) + Math.random() * 1000
}

/**
 * AGC: Check if error is a rate limit error
 */
const isRateLimitError = (error: any): boolean => {
  const errorMessage = error?.message?.toLowerCase() || ''
  const errorCode = error?.code
  
  return (
    errorCode === -32016 ||
    errorCode === 429 ||
    errorMessage.includes('rate limit') ||
    errorMessage.includes('429') ||
    errorMessage.includes('too many requests')
  )
}

/**
 * AGC: Execute RPC call with rate limiting, retry, and caching
 */
export async function executeWithRateLimit<T>(
  cacheKey: string,
  fn: () => Promise<T>,
  config: Partial<RateLimitConfig> = {}
): Promise<T> {
  const cfg = { ...DEFAULT_CONFIG, ...config }
  
  // AGC: Check cache first
  const cached = cache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < cfg.cacheDurationMs) {
    if (SHOULD_LOG_CACHE_HITS) {
      console.log(`✅ Cache hit for ${cacheKey}`)
    }
    return cached.data
  }
  
  // AGC: Execute with retry logic
  let lastError: any
  
  for (let attempt = 0; attempt < cfg.maxRetries; attempt++) {
    try {
      const result = await fn()
      
      // AGC: Cache successful result
      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      })
      
      if (attempt > 0) {
        console.log(`✅ RPC call succeeded after ${attempt + 1} attempt(s)`)
      }
      
      return result
    } catch (error: any) {
      lastError = error
      
      // AGC: Check if it's a rate limit error
      if (isRateLimitError(error)) {
        if (attempt < cfg.maxRetries - 1) {
          const delay = getBackoffDelay(attempt, cfg.baseDelayMs)
          console.warn(`⚠️ RPC rate limited (attempt ${attempt + 1}/${cfg.maxRetries}), retrying in ${Math.round(delay)}ms...`)
          await new Promise(resolve => setTimeout(resolve, delay))
          continue
        } else {
          console.error(`❌ RPC rate limit exceeded after ${cfg.maxRetries} attempts`)
          throw new Error(`RPC rate limited: ${error.message}. Please wait a few seconds and try again.`)
        }
      }
      
      // AGC: For non-rate-limit errors, check if we have cached data
      if (cached) {
        console.warn(`⚠️ Using stale cache for ${cacheKey} due to error:`, error.message)
        return cached.data
      }
      
      // AGC: Non-rate-limit error, throw immediately
      throw error
    }
  }
  
  throw lastError
}

/**
 * AGC: Clear all cached RPC data
 */
export function clearRpcCache(): void {
  cache.clear()
  console.log('🗑️ RPC cache cleared')
}

/**
 * AGC: Clear specific cache entry
 */
export function clearCacheEntry(key: string): void {
  cache.delete(key)
  console.log(`🗑️ Cache entry cleared: ${key}`)
}

/**
 * AGC: Get cache statistics
 */
export function getCacheStats(): { size: number; keys: string[] } {
  return {
    size: cache.size,
    keys: Array.from(cache.keys())
  }
}



